/**
 * Pure helpers for the electrical emergency & standby power output (NEC Article
 * 517/700). The engine emits an optional `emergency_system` (source + schedule)
 * plus an `emergency_branch` map on the raw electrical Output — present only
 * when the opt-in emergency stage ran, absent otherwise (emergency-off outputs
 * serialize byte-identically). These helpers reshape those into the flat rows
 * the results view renders, tolerating absent data (returns empty rows).
 *
 * The per-element branch classification is a HEURISTIC screening estimate: a
 * licensed engineer must confirm each branch assignment. `requiresEngineerConfirmation`
 * makes that visible so the UI surfaces the heuristic instead of hiding it.
 * Deterministic, no IO — unit-testable as plain functions (mirrors ../mep/grounding).
 */
import type { Ats, EmergencyBranch, EmergencyScheduleRow, EmergencySystem } from "../api/types";

/** One flattened schedule row for the emergency-system table. */
export interface EmergencyScheduleTableRow {
  kind: string;
  label: string;
  size: string;
  citation: string;
  /** `10s_auto` | `delayed_auto`; empty string when the row carries none. */
  transferMode: string;
}

/** One flattened per-element branch classification row. */
export interface EmergencyBranchTableRow {
  /** The engine's NAMESPACED element key (`circuit:` / `exit_sign:` / `hvac_load:`). */
  elementId: string;
  /** The element source (`circuit` / `exit_sign` / `hvac_load`) parsed from the key. */
  source: string;
  /** The bare element id (the key with the source prefix stripped). */
  element: string;
  /** The assigned NEC 517.30 branch token. */
  branch: EmergencyBranch;
  /** Human-facing branch label (e.g. "Life-Safety"). */
  branchLabel: string;
}

/** The emergency output split into the rendered sections. */
export interface EmergencyView {
  /** Source summary line, or `null` when the stage did not run. */
  source: {
    sourceKind: string;
    ampacityA: number;
    systemClass: string;
    durationMin: number;
  } | null;
  /** Schedule rows (source / ATS / branch). */
  schedule: EmergencyScheduleTableRow[];
  /** Per-element branch classification, sorted by element id (deterministic). */
  branches: EmergencyBranchTableRow[];
  /** Automatic transfer switches. */
  ats: Ats[];
  /**
   * True when a per-element branch classification is present — the heuristic
   * that a licensed engineer must confirm. Surfaced so the UI shows the flag.
   */
  requiresEngineerConfirmation: boolean;
  /** True when there is nothing to render (stage off / empty). */
  isEmpty: boolean;
}

const BRANCH_LABELS: Record<EmergencyBranch, string> = {
  life_safety: "Life-Safety",
  critical: "Critical",
  equipment: "Equipment",
};

/** Human-facing label for a branch token; falls back to the raw token. */
export function emergencyBranchLabel(branch: string): string {
  return BRANCH_LABELS[branch as EmergencyBranch] ?? branch;
}

/**
 * Normalize the raw emergency output into the flat view the results view
 * renders. Both `emergency_system` and `emergency_branch` are absent/empty when
 * the emergency stage did not run, so a missing input yields an empty view with
 * `isEmpty: true` — the UI shows a placeholder instead of an empty table.
 */
export function emergencyView(
  system: EmergencySystem | null | undefined,
  branchMap: Record<string, EmergencyBranch> | null | undefined,
  ats: Ats[] | null | undefined,
): EmergencyView {
  const scheduleRows: EmergencyScheduleRow[] =
    system && Array.isArray(system.schedule?.rows) ? system.schedule.rows : [];
  const schedule = scheduleRows.map((r) => ({
    kind: r.kind,
    label: r.label,
    size: r.size,
    citation: r.citation,
    transferMode: r.transfer_mode ?? "",
  }));

  // The branch map is keyed by the NAMESPACED element id; split off the source
  // prefix so the table can show the source + bare element separately. Sorting
  // by key keeps the render deterministic regardless of object key order.
  const branchEntries = branchMap && typeof branchMap === "object" ? Object.entries(branchMap) : [];
  const branches = branchEntries
    .map(([elementId, branch]) => {
      const idx = elementId.indexOf(":");
      const source = idx >= 0 ? elementId.slice(0, idx) : "";
      const element = idx >= 0 ? elementId.slice(idx + 1) : elementId;
      return {
        elementId,
        source,
        element,
        branch,
        branchLabel: emergencyBranchLabel(branch),
      };
    })
    .sort((a, b) => a.elementId.localeCompare(b.elementId));

  const atsRows = Array.isArray(ats) ? ats : [];

  const source = system
    ? {
        sourceKind: system.source_kind,
        ampacityA: system.ampacity_a,
        systemClass: system.system_class,
        durationMin: system.duration_min,
      }
    : null;

  return {
    source,
    schedule,
    branches,
    ats: atsRows,
    requiresEngineerConfirmation: branches.length > 0,
    isEmpty: source === null && schedule.length === 0 && branches.length === 0,
  };
}
